import React from 'react';

import { BaseInputProps, StyledTextField } from './StyledTextField';

export interface DateInputProps extends BaseInputProps {
  /** Valor en formato YYYY-MM-DD o ISO 8601 (ej: "2026-03-09T00:00:00.000Z") */
  value: string;
  /** Callback cuando cambia el valor */
  onChange: (value: string) => void;
  /** Fecha mínima permitida en formato YYYY-MM-DD */
  minDate?: string;
  /** Fecha máxima permitida en formato YYYY-MM-DD */
  maxDate?: string;
  /** Nombre del campo para formularios */
  name?: string;
}

/**
 * Convierte una fecha en formato ISO o YYYY-MM-DD a formato YYYY-MM-DD
 */
const formatDateForInput = (dateString: string | undefined): string => {
  if (!dateString) return '';
  // Si ya está en formato YYYY-MM-DD, devolverlo tal cual
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    return dateString;
  }
  // Convertir formato ISO a YYYY-MM-DD
  try {
    return new Date(dateString).toISOString().split('T')[0];
  } catch {
    return dateString;
  }
};

/**
 * Características:
 * - Usa input type="date" nativo de HTML5
 * - Formato YYYY-MM-DD (acepta también ISO 8601 y lo convierte internamente)
 * - Soporte para rango de fechas min/max
 * - Validación visual de errores
 */
export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
  fullWidth = true,
  placeholder,
  minDate,
  maxDate,
  name,
}) => {
  const formattedValue = formatDateForInput(value);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Validar rango si está definido
    if (minDate && newValue < minDate) {
      return;
    }
    if (maxDate && newValue > maxDate) {
      return;
    }

    onChange(newValue);
  };

  return (
    <StyledTextField
      type="date"
      value={formattedValue}
      onChange={handleChange}
      label={label}
      disabled={disabled}
      error={error}
      helperText={helperText}
      required={required}
      fullWidth={fullWidth}
      placeholder={placeholder}
      name={name}
      InputLabelProps={{
        shrink: true,
      }}
      inputProps={{
        min: minDate,
        max: maxDate,
      }}
    />
  );
};
